import { useState, useRef, FormEvent } from 'react';

const WA_URL = 'https://wa.me/573228000501';
const FORM_URL = 'https://readdy.ai/api/form/d75bs0r41deorl02f970';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });

    setStatus('sending');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contacto" className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block font-sans text-xs tracking-[0.25em] text-gold uppercase mb-3">
            Encuéntranos
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-charcoal mb-4">
            Visítanos en Nuestra Tienda
          </h2>
          <div className="w-10 h-0.5 bg-gold mx-auto mb-5" />
          <p className="font-sans text-sm text-gray-medium max-w-md mx-auto">
            Visítanos en nuestro punto físico y conoce nuestras colecciones de cerca.
          </p>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-14">
          {[
            { icon: 'ri-customer-service-2-line', label: 'Atención personalizada' },
            { icon: 'ri-medal-line', label: 'Productos exclusivos' },
            { icon: 'ri-whatsapp-line', label: 'Compra fácil por WhatsApp' },
          ].map((b) => (
            <div
              key={b.label}
              className="flex items-center gap-2 bg-sand/40 border border-gold/20 rounded-full px-5 py-2"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${b.icon} text-gold text-base`} />
              </div>
              <span className="font-sans text-xs font-medium text-charcoal whitespace-nowrap">
                {b.label}
              </span>
            </div>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid md:grid-cols-2 gap-10 md:gap-14">
          {/* Left: Map + Info */}
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl overflow-hidden border border-gold/30 h-[340px] md:h-[380px]">
              <iframe
                src="https://maps.google.com/maps?q=4.128444219926199,-73.61767191424615&z=18&output=embed&hl=es"
                width="100%"
                height="100%"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación LauraSoto_Shop - Carrera 19 Nº4D-37, Barrio Villa Alejandra, Villavicencio, Meta"
              />
            </div>

            <div className="bg-sand/30 rounded-2xl p-6 border border-gold/20 flex flex-col gap-4">
              {[
                {
                  icon: 'ri-map-pin-2-fill',
                  title: 'Dirección',
                  content: 'Carrera 19 Nº4D-37\nBarrio Villa Alejandra\nVillavicencio, Meta, Colombia',
                  type: 'text',
                },
                {
                  icon: 'ri-phone-fill',
                  title: 'Teléfono / WhatsApp',
                  content: '+57 322 8000 501',
                  href: 'tel:+573228000501',
                  type: 'link',
                },
                {
                  icon: 'ri-instagram-fill',
                  title: 'Instagram',
                  content: '@laurasoto_shop',
                  href: 'https://www.instagram.com/laurasoto_shop',
                  type: 'link',
                },
                {
                  icon: 'ri-tiktok-fill',
                  title: 'TikTok',
                  content: '@laurasoto_shop',
                  href: 'https://www.tiktok.com/@laurasoto_shop',
                  type: 'link',
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-gold/15 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-gold text-xl`} />
                  </div>
                  <div>
                    <p className="font-sans font-semibold text-charcoal text-sm mb-1">
                      {item.title}
                    </p>
                    {item.type === 'link' && item.href ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-sans text-gray-medium text-sm hover:text-gold transition-colors"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <p className="font-sans text-gray-medium text-sm leading-relaxed whitespace-pre-line">
                        {item.content}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-4 px-6 rounded-xl font-sans font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-3 hover:scale-[1.02] cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill text-2xl" />
                Escríbenos por WhatsApp
              </a>
              <a
                href="https://www.instagram.com/laurasoto_shop"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 text-white py-4 px-6 rounded-xl font-sans font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-3 hover:opacity-90 hover:scale-[1.02] cursor-pointer whitespace-nowrap"
              >
                <i className="ri-instagram-fill text-2xl" />
                Síguenos en Instagram
              </a>
              <a
                href="https://www.tiktok.com/@laurasoto_shop"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-[#010101] via-[#161616] to-[#2a2a2a] text-white py-4 px-6 rounded-xl font-sans font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-3 hover:opacity-80 hover:scale-[1.02] cursor-pointer whitespace-nowrap"
              >
                <i className="ri-tiktok-fill text-2xl" />
                Síguenos en TikTok
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <div className="bg-white border border-gray-100 rounded-2xl p-8 md:p-10">
              <h3 className="font-serif text-2xl font-bold text-charcoal mb-2">
                Envíanos un mensaje
              </h3>
              <p className="font-sans text-sm text-gray-medium mb-8">
                Cuéntanos qué necesitas y te respondemos pronto.
              </p>

              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600" />
                  </div>
                  <h4 className="font-serif text-xl font-bold text-charcoal mb-2">
                    ¡Mensaje enviado!
                  </h4>
                  <p className="font-sans text-sm text-gray-medium">
                    Te responderemos muy pronto. También puedes escribirnos por WhatsApp.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 text-gold font-sans text-sm hover:underline cursor-pointer"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  data-readdy-form={true}
                  id="contacto-laurasoto"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div>
                    <label className="block font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-2">
                      Nombre
                    </label>
                    <input
                      name="name"
                      placeholder="Tu nombre completo"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 font-sans text-sm text-charcoal placeholder-gray-300 focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-2">
                      Correo electrónico
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="tu@correo.com"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 font-sans text-sm text-charcoal placeholder-gray-300 focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-2">
                      Mensaje
                    </label>
                    <textarea
                      name="message"
                      placeholder="¿En qué podemos ayudarte?"
                      required
                      maxLength={500}
                      rows={4}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 font-sans text-sm text-charcoal placeholder-gray-300 focus:outline-none focus:border-gold transition-colors resize-none"
                    />
                    <p className="font-sans text-xs text-gray-medium text-right mt-1">
                      {charCount}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <p className="text-sm text-red-500 text-center">
                      Ocurrió un error. Por favor intenta nuevamente.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full bg-charcoal hover:bg-charcoal/90 text-white py-4 rounded-xl font-sans font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    <i className="ri-send-plane-fill text-lg" />
                    {status === 'sending' ? 'Enviando...' : 'Enviar mensaje'}
                  </button>
                </form>
              )}

              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <p className="font-sans text-xs text-gray-medium mb-3">
                  ¿Prefieres una respuesta más rápida?
                </p>
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#25D366] font-sans font-semibold text-sm hover:underline cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-fill text-lg" />
                  Escríbenos directamente por WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
